import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getDateNotLess, getDateScaled } from '../functions';
import { useRouting } from '../routing/routingProvider';
import { RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterGantScroll } from '../store/slices/gantScrollSlice';
import { CalendarScaleType, HeaderType, ModeTimeLineType } from '../types';
import { useSettings } from './useSettings';

const SCROLL_END_DEBOUNCE_MS = 150;

let getScrollX: ((ts: DateTime, calendarDaysOffset: number, scheduleScale: HeaderType) => number) | null = null;
let isInitialScrollByDateSet = false;

export const useSyncCalendarDate = () => {
    const dispatch = useDispatch();

    const startDateExtend = useSelector<RootState, string>((state) => state.gant.startDateExtend);
    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);
    const currentScrollX = useSelector<RootState, number>((state) => state.gantScroll.currentScrollX);
    const calendarDaysOffset = useSelector<RootState, number>((state) => state.gant.calendarOffsetDays);
    const modeTimeline = useSelector<RootState, ModeTimeLineType>((state) => state.gant.modeTimeLine);
    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const currentCalendarDate = useSelector<RootState, string>((state) => state.calendar.currentDate);
    const allowCurrentDateSync = useSelector<RootState, boolean>((state) => state.calendar.allowSync);
    const scheduleScale = useSelector<RootState, CalendarScaleType>((state) => state.calendar.scheduleScale);

    const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

    const { getUrlPath, getParams } = useRouting();
    const isGantPage = getUrlPath().endsWith('timeline');
    const isCalendarPage = getUrlPath().endsWith('calendar');

    const { saveUserSettings, isSettingsLoaded } = useSettings();

    useEffect(() => {
        if (
            !isGantPage ||
            !isInitialScrollByDateSet ||
            Number.isNaN(currentScrollX) ||
            !startDateExtend ||
            allowCurrentDateSync
        ) {
            return;
        }

        if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current);
        }
        const timelineStart = DateTime.fromISO(startDateExtend);
        if (getScrollX) {
            dispatch(
                setterGantScroll({
                    currentScrollX: getScrollX(timelineStart, calendarDaysOffset, scheduleScale),
                }),
            );
            getScrollX = null;
            return;
        }

        scrollTimeout.current = setTimeout(() => {
            const daysOffset = Math.round(currentScrollX / widthColumn);

            const correspondingDate = timelineStart.plus({ days: daysOffset });

            const correspondingDateWithOffset = correspondingDate.plus({ days: calendarDaysOffset });

            const currentDayWithScale = getDateNotLess({
                currentDate: getDateScaled(correspondingDateWithOffset, scheduleScale),
                notLessDate: correspondingDate,
                fallbackDate: getDateScaled(correspondingDateWithOffset.plus({ days: 7 }), scheduleScale),
            });

            const currentDate = currentDayWithScale.toISODate() ?? undefined;

            const currentCalendarDateToCompare =
                scheduleScale === 'day'
                    ? currentCalendarDate
                    : getDateScaled(currentCalendarDate, scheduleScale).toISODate();

            if (currentCalendarDateToCompare !== currentDate) {
                dispatch(
                    setterCalendar({
                        currentDate,
                    }),
                );
                saveUserSettings({
                    calendarCurrentDate: correspondingDate.toISODate(),
                    gantScrollX: currentScrollX,
                });
            }
        }, SCROLL_END_DEBOUNCE_MS);

        return () => {
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
        };
    }, [startDateExtend, currentScrollX, isSettingsLoaded]);

    useEffect(() => {
        if (isInitialScrollByDateSet && !isCalendarPage && !allowCurrentDateSync) {
            return;
        }

        const { cldd } = getParams('initial');
        const timelineStart = DateTime.fromISO(startDateExtend);
        const calendarDate =
            !isInitialScrollByDateSet && cldd
                ? DateTime.fromISO(cldd as string)
                : DateTime.fromISO(currentCalendarDate);

        if (calendarDate.isValid) {
            saveUserSettings({
                calendarCurrentDate: calendarDate.toISODate(),
            });
        }

        const getNewScrollX = (ts: DateTime, calendarDaysOffset: number, scheduleScale: HeaderType) => {
            const calendarDateWithOffset = getDateScaled(
                calendarDate.minus({ days: calendarDaysOffset }),
                scheduleScale,
            );

            const daysDiff = Math.max(calendarDateWithOffset.diff(ts, 'days').as('days'), 0);
            return daysDiff * widthColumn;
        };

        if (!timelineStart.isValid) {
            getScrollX = getNewScrollX;
        }
        if (!timelineStart.isValid || !calendarDate.isValid) {
            return;
        }

        const newCurrentScrollX = getNewScrollX(timelineStart, calendarDaysOffset, scheduleScale);

        if (allowCurrentDateSync) {
            getScrollX = null;
        }

        dispatch(
            setterCalendar({
                allowSync: false,
            }),
        );
        dispatch(
            setterGantScroll({
                currentScrollX: newCurrentScrollX,
            }),
        );

        saveUserSettings({
            calendarCurrentDate: calendarDate.toISODate(),
            gantScrollX: newCurrentScrollX,
        });

        setTimeout(() => (isInitialScrollByDateSet = true), 3000);
    }, [currentCalendarDate, widthColumn, isSettingsLoaded, startDateExtend]);

    useEffect(() => {
        saveUserSettings({
            calendarCurrentDate: startDateExtend,
            gantWidthColumn: widthColumn,
            gantModeTimeline: modeTimeline,
            gantHeaderType: headerType,
        });
    }, [startDateExtend, widthColumn, isSettingsLoaded, modeTimeline, headerType]);
};
